import type { ICommand } from '../../../core/commands/ICommand';
import type { Edge, Node } from '../../../core/models/scene';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import { ShapeMapAdapter } from '../../../lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '../../../lib/diagram/shapeGeometry';
import { useDiagramStore } from '../../../stores/diagramStore';

export class ClearOrgCanvasCommand implements ICommand {
  id: string;
  name = 'Clear Org Canvas';
  description = 'Clear starter org chart content';
  timestamp = Date.now();

  constructor(
    private readonly previousNodes: Node[],
    private readonly previousEdges: Edge[],
  ) {
    this.id = `clear-org-canvas-${Date.now()}`;
  }

  execute(): void {
    const mutationManager = getNodeMutationManager();
    const ydoc = mutationManager.getDoc();

    if (ydoc) {
      const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(ydoc.getMap('shapes')));
      const connectorsMap = ydoc.getMap('connectors');

      ydoc.transact(() => {
        this.previousEdges.forEach((edge) => connectorsMap.delete(edge.id));
        this.previousNodes.forEach((node) => shapes.delete(node.id));
      }, 'local');
    } else {
      if (this.previousEdges.length > 0) {
        mutationManager.batchDeleteEdges(this.previousEdges.map((edge) => edge.id));
      }
      if (this.previousNodes.length > 0) {
        mutationManager.batchDeleteNodes(this.previousNodes.map((node) => node.id));
      }
    }

    useDiagramStore.getState().setSelected(new Set());
  }

  undo(): void {
    const mutationManager = getNodeMutationManager();
    const ydoc = mutationManager.getDoc();

    if (ydoc) {
      const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(ydoc.getMap('shapes')));
      const connectorsMap = ydoc.getMap('connectors');

      ydoc.transact(() => {
        this.previousNodes.forEach((node) => shapes.upsert(node as unknown as Record<string, unknown>));
        this.previousEdges.forEach((edge) => connectorsMap.set(edge.id, edge));
      }, 'local');
    } else {
      if (this.previousNodes.length > 0) {
        mutationManager.batchAddNodes(this.previousNodes);
      }
      if (this.previousEdges.length > 0) {
        mutationManager.batchAddEdges(this.previousEdges);
      }
    }
  }

  redo(): void {
    this.execute();
  }
}
